import { useState, useEffect } from 'react'

const STORAGE_KEY = 'reverend_welcomed'
const VISIBLE_MS = 7000   // how long the screen stays fully visible
const FADE_MS = 1200      // duration of the fade-out (keep in sync with CSS transition)

/**
 * One-time onboarding overlay shown on the very first app start.
 * Stays visible for 5s, then slowly fades out and unmounts itself.
 * A localStorage flag makes sure returning users never see it again.
 */
export default function WelcomeOverlay() {
  // Only render at all if the user has never been welcomed before.
  const [show, setShow] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== '1'
    } catch {
      return false // private mode / storage blocked → skip onboarding
    }
  })
  const [fading, setFading] = useState(false)

  useEffect(() => {
    if (!show) return

    try {
      localStorage.setItem(STORAGE_KEY, '1')
    } catch {
      /* ignore storage errors */
    }

    const fadeTimer = setTimeout(() => setFading(true), VISIBLE_MS)
    const removeTimer = setTimeout(() => setShow(false), VISIBLE_MS + FADE_MS)

    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(removeTimer)
    }
  }, [show])

  if (!show) return null

  // Tapping dismisses immediately (still fades out gracefully).
  const dismiss = () => {
    setFading(true)
    setTimeout(() => setShow(false), FADE_MS)
  }

  return (
    <div
      className={`welcome-overlay${fading ? ' fading' : ''}`}
      onClick={dismiss}
      role="dialog"
      aria-label="Welcome to Reverend"
    >
      <div className="welcome-card">
        <span className="welcome-cross">✝</span>
        <h1 className="welcome-title">Welcome to Reverend</h1>
        <p className="welcome-sub">A place to confess — anonymous &amp; honest.</p>

        <ul className="welcome-points">
          <li><span className="welcome-icon">🕊️</span> Share what's on your mind, completely anonymously.</li>
          <li><span className="welcome-icon">👍</span> Vote on confessions to give them karma.</li>
          <li><span className="welcome-icon">💬</span> Reply and react — no account, no names.</li>
        </ul>

        <p className="welcome-hint">Tap anywhere to begin</p>
      </div>
    </div>
  )
}
